/**
 * Shared email-verification status fetch.
 *
 * Coalesces concurrent in-flight queries (guards + gates in one navigation) without
 * memoizing a boolean for the whole SPA session — a stale `false` would keep
 * trapping sitters who verified in another tab.
 */
import { getSharedApolloClient } from '@/lib/apollo_setup';
import { EMAIL_VERIFICATION_STATUS } from '@/graphql/account/queries';

export type EmailVerificationStatusResult = {
  verified: boolean;
  emailVerificationRequired: boolean;
  excludeFromEmailVerificationExperiment: boolean;
};

let inFlight: Promise<EmailVerificationStatusResult> | null = null;

export function invalidateEmailVerificationStatus(): void {
  inFlight = null;
}

export function getEmailVerificationStatus(): Promise<EmailVerificationStatusResult> {
  if (!inFlight) {
    inFlight = fetchEmailVerificationStatus().finally(() => {
      inFlight = null;
    });
  }

  return inFlight;
}

export async function getEmailVerified(): Promise<boolean> {
  const status = await getEmailVerificationStatus();
  return status.verified;
}

async function fetchEmailVerificationStatus(): Promise<EmailVerificationStatusResult> {
  const apolloClient = getSharedApolloClient();
  const { data } = await apolloClient.query({
    query: EMAIL_VERIFICATION_STATUS,
    fetchPolicy: 'network-only',
  });

  return {
    verified: !!data?.emailVerificationStatus?.verified,
    emailVerificationRequired: !!data?.emailVerificationStatus?.emailVerificationRequired,
    excludeFromEmailVerificationExperiment: !!data?.emailVerificationStatus?.excludeFromEmailVerificationExperiment,
  };
}
